/**
 * CalendarTableTools.jsx - the controls that edit the Manpower calendar's
 * tables (10/16/26): a table's name (click Rename), its "Counts toward
 * utilization" switch, up / down, delete (only when empty), the "Add table"
 * form, and each engineer's "Move to" menu.
 *
 * Everything works from the keyboard: the Move to menu is a plain <select>,
 * so dragging a name onto another table is a shortcut, never the only way.
 * State lives in useCalendarTables; these only call it.
 */
import { useState } from 'react';

const HEADER_BTN = {
  padding: '4px 9px',
  borderRadius: 6,
  fontSize: 10.5,
  fontWeight: 800,
  fontFamily: 'Montserrat',
  border: '1px solid rgba(255,255,255,0.55)',
  background: 'transparent',
  color: '#fff',
  cursor: 'pointer',
};
const INPUT = {
  padding: '5px 8px',
  borderRadius: 6,
  border: '1px solid #C8D3C3',
  fontSize: 11.5,
  fontFamily: 'Montserrat',
  fontWeight: 700,
  minWidth: 180,
};

/** A table's name; with `onRename`, a Rename button that turns it into a field. */
export function TableTitle({ name, onRename }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(name);
  if (!editing) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        {name}
        {onRename && (
          <button
            type="button"
            aria-label={`Rename ${name}`}
            title="Rename this table"
            onClick={() => {
              setValue(name);
              setEditing(true);
            }}
            style={{ ...HEADER_BTN, padding: '2px 7px', fontSize: 9.5, fontWeight: 700, opacity: 0.85 }}
          >
            Rename
          </button>
        )}
      </span>
    );
  }
  const save = () => {
    const v = value.replace(/\s+/g, ' ').trim();
    setEditing(false);
    if (v && v !== name) onRename(v);
  };
  return (
    <form
      onSubmit={(ev) => {
        ev.preventDefault();
        save();
      }}
      style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}
    >
      <input
        aria-label="Table name"
        autoFocus
        maxLength={60}
        value={value}
        onChange={(ev) => setValue(ev.target.value)}
        onKeyDown={(ev) => {
          if (ev.key === 'Escape') setEditing(false);
        }}
        style={{ ...INPUT, color: '#0D0D0D' }}
      />
      <button type="submit" style={{ ...HEADER_BTN, background: '#fff', color: '#0F3D14' }}>
        Save
      </button>
      <button type="button" onClick={() => setEditing(false)} style={HEADER_BTN}>
        Cancel
      </button>
    </form>
  );
}

/** The switch, up / down and delete, in a table's dark header bar. */
export function TableHeaderTools({ table, first, last, empty, onlyTable, onCounts, onShift, onDelete }) {
  const why = onlyTable ? 'The calendar needs at least one table' : 'Move everyone out of this table first';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <label
        title="Only engineers in tables with this on are counted in utilization (interns never are)"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10.5, fontWeight: 700, cursor: 'pointer', marginRight: 4 }}
      >
        <input type="checkbox" checked={!!table.countsUtilization} onChange={(ev) => onCounts(ev.target.checked)} />
        Counts toward utilization
      </label>
      <button
        type="button"
        aria-label={`Move ${table.name} up`}
        title="Move this table up"
        disabled={first}
        onClick={() => onShift(-1)}
        style={{ ...HEADER_BTN, opacity: first ? 0.35 : 1, cursor: first ? 'default' : 'pointer' }}
      >
        {'↑'}
      </button>
      <button
        type="button"
        aria-label={`Move ${table.name} down`}
        title="Move this table down"
        disabled={last}
        onClick={() => onShift(1)}
        style={{ ...HEADER_BTN, opacity: last ? 0.35 : 1, cursor: last ? 'default' : 'pointer' }}
      >
        {'↓'}
      </button>
      <button
        type="button"
        aria-label={`Delete ${table.name}`}
        disabled={!empty || onlyTable}
        title={!empty || onlyTable ? why : 'Delete this empty table'}
        onClick={() => {
          if (window.confirm(`Delete the table "${table.name}"?`)) onDelete();
        }}
        style={{
          ...HEADER_BTN,
          borderColor: '#FFCDD2',
          color: '#FFCDD2',
          opacity: !empty || onlyTable ? 0.35 : 1,
          cursor: !empty || onlyTable ? 'not-allowed' : 'pointer',
        }}
      >
        Delete
      </button>
    </span>
  );
}

/** One engineer's "Move to" menu: every other table. */
export function MoveToSelect({ name, tables, current, onMove }) {
  const others = tables.filter((t) => t.id !== current);
  if (!others.length) return null;
  return (
    <select
      aria-label={`Move ${name} to another table`}
      title="Move to another table"
      value=""
      onChange={(ev) => {
        if (ev.target.value) onMove(ev.target.value);
      }}
      onClick={(ev) => ev.stopPropagation()}
      style={{
        fontSize: 9,
        fontWeight: 700,
        fontFamily: 'Montserrat',
        color: '#1B5E20',
        maxWidth: 70,
        padding: '1px 2px',
        border: '1px solid #DDE4D8',
        borderRadius: 4,
        background: '#fff',
        cursor: 'pointer',
        flexShrink: 0,
      }}
    >
      <option value="">Move to…</option>
      {others.map((t) => (
        <option key={t.id} value={t.id}>
          {t.name}
        </option>
      ))}
    </select>
  );
}

/** "+ Add table": a button that opens a name field. */
export function AddTableButton({ onAdd }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const btn = {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    borderRadius: 6,
    border: '1px solid #E5E9DD',
    background: '#fff',
    color: '#1B5E20',
    fontSize: 11,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'Montserrat',
  };
  if (!open)
    return (
      <button type="button" onClick={() => setOpen(true)} style={btn}>
        + Add table
      </button>
    );
  const add = async () => {
    const v = value.replace(/\s+/g, ' ').trim();
    if (!v) return;
    const out = await onAdd(v);
    if (out) {
      setValue('');
      setOpen(false);
    }
  };
  return (
    <form
      onSubmit={(ev) => {
        ev.preventDefault();
        add();
      }}
      style={{ display: 'flex', alignItems: 'center', gap: 6 }}
    >
      <input
        aria-label="New table name"
        placeholder="New table name"
        autoFocus
        maxLength={60}
        value={value}
        onChange={(ev) => setValue(ev.target.value)}
        onKeyDown={(ev) => {
          if (ev.key === 'Escape') setOpen(false);
        }}
        style={INPUT}
      />
      <button type="submit" style={{ ...btn, background: '#1B5E20', color: '#fff', border: '1px solid #0F3D14' }}>
        Add
      </button>
      <button type="button" onClick={() => setOpen(false)} style={btn}>
        Cancel
      </button>
    </form>
  );
}
